import { useEffect, useRef } from "react";
import { idbGet, idbPut, idbKeys, idbDelete } from "@utils/idb";
import { ConsultationService } from "@services/consultationService";

// Unique per app run — resets retry counters & per-run "non-retriable" on reload
const RUN_ID =
  (typeof crypto !== "undefined" && crypto.randomUUID && crypto.randomUUID()) ||
  String(Date.now());

export function useUploadQueue({ backendUrl, consultationManager, busyState }) {
  const serviceRef = useRef(new ConsultationService(backendUrl));
  const runningRef = useRef(false);
  const retryIntervalRef = useRef(null);
  const inFlightSegIdsRef = useRef(new Set());

  useEffect(() => {
    rehydrateUI().then(flushAll);
    const onOnline = () => flushAll();
    retryIntervalRef.current = setInterval(() => {
      console.log("🕒 Periodic retry check...");
      flushFailedSessions();
    }, 180000); // 2 minutes
    window.addEventListener("online", onOnline);
    return () => {
      window.removeEventListener("online", onOnline);
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
      }
    };
  }, []);

  async function flushFailedSessions() {
    if (runningRef.current) return;
    runningRef.current = true;

    try {
      const sessionIds = await idbKeys("sessions");
      const flushPromises = sessionIds.map(async (sessionId) => {
        const manifest = await idbGet("sessions", sessionId);
        if (manifest && !manifest.completed) {
          console.log("🔄 Periodic retry for session:", sessionId);
          await flushSession(String(sessionId));
        }
      });

      await Promise.allSettled(flushPromises);
    } finally {
      runningRef.current = false;
    }
  }

  async function rehydrateUI() {
    const savedPending = await idbGet("ui", "pendingGroups");
    if (savedPending?.length) {
      consultationManager.setGroupedConsultations((prev) => prev ?? []);
      consultationManager.setViewMode("new");
      consultationManager.setSelectedConsultation(null);
      consultationManager.setPendingGroups(savedPending);
    }
  }

  async function flushAll() {
    if (runningRef.current) return;
    runningRef.current = true;
    try {
      const sessionIds = await idbKeys("sessions");
      console.log("🔄 Flushing all sessions:", sessionIds);
      for (const sessionId of sessionIds) {
        await flushSession(String(sessionId));
      }
    } finally {
      runningRef.current = false;
    }
  }

  async function flushSession(sessionId) {
    const manifest = await idbGet("sessions", sessionId);
    if (!manifest) return;

    // Initialize or reset retryInfo if app reloaded
    if (!manifest.retryInfo) {
      manifest.retryInfo = {
        retryCount: 0,
        maxRetries: 5,
        lastRetryTime: null,
        retryInterval: 120000,
        runId: RUN_ID,
      };
    } else if (manifest.retryInfo.runId !== RUN_ID) {
      // New app run → reset retry counter
      manifest.retryInfo.runId = RUN_ID;
      manifest.retryInfo.retryCount = 0;
      await idbPut("sessions", sessionId, manifest);
    }

    const retryInfo = manifest.retryInfo;
    const now = Date.now();

    // 🔎 Pending work only counts for this run if it's not marked non-retriable FOR THIS RUN
    const hasPendingUpload = (manifest.segments || []).some(
      (seg) =>
        !seg.uploaded &&
        !(seg.nonRetriableRunId === RUN_ID) &&
        (!seg.nextAttemptAt || seg.nextAttemptAt <= now)
    );

    const isEmergencySession = manifest.emergencyFlush && !manifest.completed;
    const underCap = retryInfo.retryCount < retryInfo.maxRetries;

    if (hasPendingUpload) {
      if (!isEmergencySession && !underCap) {
        console.log(
          "⛔ Max retries reached for this run — will resume on next reload:",
          sessionId
        );
        return; // stop this run; new RUN_ID on reload will try again
      }

      retryInfo.retryCount++;
      retryInfo.lastRetryTime = now;
      await idbPut("sessions", sessionId, manifest);
    } else {
      console.log("ℹ️ No pending segments to upload for", sessionId);
    }

    try {
      const hasFinalSegment =
        manifest.segments.some((seg) => seg.isFinal) ||
        manifest.emergencyFlush ||
        manifest.isFinal;

      (manifest.segments || []).sort((a, b) => a.index - b.index);
      console.log("🔄 Flushing session:", {
        sessionId,
        segments: manifest.segments.length,
        hasFinalSegment,
        h: manifest.completed,
      });

      for (const seg of manifest.segments) {
        // Skip only if uploaded OR marked non-retriable for THIS RUN
        if (seg.uploaded || seg.nonRetriableRunId === RUN_ID) continue;
        if (inFlightSegIdsRef.current.has(seg.id)) continue;

        const blob = await idbGet("segments", seg.id);
        if (!blob) {
          // Don't pretend uploaded; just skip for this run
          seg.nonRetriableRunId = RUN_ID;
          seg.lastError = "Missing segment blob in IDB";
          await idbPut("sessions", sessionId, manifest);
          continue;
        }

        try {
          inFlightSegIdsRef.current.add(seg.id);
          busyState.begin("uploading");
          const res = await serviceRef.current.uploadSegment(
            blob,
            seg.isFinal,
            {
              sessionId,
              segmentIndex: seg.index,
            }
          );

          if (res && res.success === false) {
            // Service already did up to 3 retries and decided to stop for now.
            seg.lastError =
              res.error || "Upload failed (service capped retries)";
            seg.nonRetriableRunId = RUN_ID; 
            await idbPut("sessions", sessionId, manifest);
            continue;
          }

          seg.uploaded = true;
          seg.lastError = undefined;

          if (!manifest.transcriptionParts) manifest.transcriptionParts = [];
          if (res?.data?.transcription) {
            manifest.transcriptionParts[seg.index] = res.data.transcription;
            console.log(`✅ Segment ${seg.index} transcription stored`);
          }

          await idbPut("sessions", sessionId, manifest);
          await idbDelete("segments", seg.id);
        } catch (e) {
          seg.nonRetriableRunId = RUN_ID;
          seg.lastError = String(e?.message || e);
          await idbPut("sessions", sessionId, manifest);
          continue;
        } finally {
          busyState.end("uploading");
          inFlightSegIdsRef.current.delete(seg.id);
        }
      }

      // If we reached here, no segment attempts remain in this pass
      retryInfo.retryCount = 0;
      await idbPut("sessions", sessionId, manifest);

      const allSegmentsUploaded = (manifest.segments || []).every(
        (seg) => seg.uploaded === true
      );

      const hasTranscriptions = !!(
        manifest.transcriptionParts &&
        manifest.transcriptionParts.some(
          (part) => part && String(part).trim().length > 0
        )
      );

      if (hasFinalSegment && allSegmentsUploaded && hasTranscriptions) {
        // ⭐ NEW: Respect per-run autofill cap
        if (manifest.autofillNonRetriableRunId === RUN_ID) {
          console.warn("⛔ Autofill capped for this run; skipping finalize:", sessionId);
          consultationManager.removeProcessingPlaceholder(sessionId);
        } else {
          console.log("🚀 Starting finalize pipeline (final segment detected)");
          await finalizePipeline(sessionId, manifest);
        }
      } else if (hasTranscriptions && !hasFinalSegment) {
        console.log("⏳ Session has transcriptions but no final segment yet");
      } else {
        console.warn("⚠️ No transcriptions available for finalization");
        consultationManager.removeProcessingPlaceholder(sessionId);
      }
    } catch (error) {
      console.error("Session processing error:", error);
    }
  }

  async function finalizePipeline(sessionId, manifest) {
    try {
      console.log(
        "🎯 FINALIZE PIPELINE: Starting full processing for session:",
        sessionId
      );
      busyState.begin("filling");

      // ⭐ NEW: Initialize / normalize per-run autofill retry info
      if (!manifest.autofillRetryInfo) {
        manifest.autofillRetryInfo = {
          retryCount: 0,
          maxRetries: 3,
          lastRetryTime: null,
          runId: RUN_ID,
        };
      } else if (manifest.autofillRetryInfo.runId !== RUN_ID) {
        manifest.autofillRetryInfo.runId = RUN_ID;
        manifest.autofillRetryInfo.retryCount = 0;
      }
      await idbPut("sessions", sessionId, manifest);

      // ⭐ NEW: If already capped for this run, exit early
      if (manifest.autofillNonRetriableRunId === RUN_ID) {
        console.warn("⛔ Autofill already capped for this run:", sessionId);
        consultationManager.removeProcessingPlaceholder(sessionId);
        return;
      }

      const merged = (manifest.transcriptionParts || [])
        .filter(Boolean)
        .join(" ");

      if (!merged) {
        console.warn("❌ No merged transcription available");
        consultationManager.removeProcessingPlaceholder(sessionId);
        await idbDelete("sessions", sessionId);
        return;
      }

      console.log("💾 Saving transcript, length:", merged.length);
      let transcriptId = manifest.transcriptId;
      const save = await serviceRef.current.saveTranscript(
        merged,
        manifest.recordingId ?? undefined
      );
      transcriptId = save.id; // unchanged on append, set on first save
      const final_transcription = save.final_transcription;
      // Mark progress
      manifest.transcriptId = transcriptId;
      await idbPut("sessions", sessionId, manifest);

      console.log("✅ Transcript saved, ID:", transcriptId);

      const formattedName = consultationManager.formatLocalDateTime();
      console.log("🔄 Calling auto-fill template...");

      const processingMode = manifest.recordingId ? "completed" : "new";
      let fileIds = [];
      try {
        // ⭐ CHANGED: Wrap autofill + polling so we can count failures
        const autofill = await serviceRef.current.autoFillTemplate(
          final_transcription,
          manifest.recordingId || transcriptId,
          formattedName,
          processingMode
        );

        console.log("✅ Auto-fill initial response:", autofill);

        const autofillData = autofill.data;
        if (autofillData?.task_id || autofillData?.status_url) {
          console.log("⏳ Starting polling for async job...");
          const statusData = await serviceRef.current.pollAutofillStatus({
            jobId: autofillData.task_id,
            statusUrl: autofillData.status_url,
          });

          console.log("✅ Polling completed:", statusData);

          if (statusData?.fileIds) {
            fileIds = statusData.fileIds;
          }
        } else if (autofillData?.fileIds) {
          fileIds = autofillData.fileIds;
        }
      } catch (autofillError) {
        // ⭐ NEW: Count failures, cap at 3 per run, then stop until refresh
        const info = manifest.autofillRetryInfo || {
          retryCount: 0,
          maxRetries: 3,
          lastRetryTime: null,
          runId: RUN_ID,
        };
        info.retryCount += 1;
        info.lastRetryTime = Date.now();
        manifest.autofillRetryInfo = info;
        manifest.lastAutofillError = String(autofillError?.message || autofillError);

        if (info.retryCount >= info.maxRetries) {
          manifest.autofillNonRetriableRunId = RUN_ID; // 🚫 block for this run
          console.error(
            `🚫 Autofill failed ${info.retryCount} times — capping for this run.`
          );
          await idbPut("sessions", sessionId, manifest);
          consultationManager.removeProcessingPlaceholder(sessionId);
          return; // stop processing until app reloads (RUN_ID changes)
        } else {
          console.warn(
            `⚠️ Autofill failed (attempt ${info.retryCount}/${info.maxRetries}). Will retry later this run.`
          );
          await idbPut("sessions", sessionId, manifest);
          consultationManager.removeProcessingPlaceholder(sessionId);
          return; // let periodic retry call finalize again later
        }
      }

      console.log("📋 Final fileIds:", fileIds);

      if (fileIds?.length >= 0) {
        console.log("✅ Success! Created files:", fileIds);
        consultationManager.removeProcessingPlaceholder(sessionId);
        manifest.completed = true;
        // ⭐ NEW: Reset autofill retries on success & clear cap
        if (manifest.autofillRetryInfo) {
          manifest.autofillRetryInfo.retryCount = 0;
          manifest.autofillRetryInfo.lastRetryTime = null;
          manifest.autofillRetryInfo.runId = RUN_ID;
        }
        manifest.autofillNonRetriableRunId = undefined;
        await idbPut("sessions", sessionId, manifest);
        await consultationManager.fetchConsultations();
      } else {
        console.warn("⚠️ No file IDs returned from autofill");
        consultationManager.removeProcessingPlaceholder(sessionId);
        // (Intentional) Not counting this as a failure; backend responded OK but produced no files.
      }
    } catch (e) {
      // ⭐ CHANGED: Treat unexpected finalize errors as autofill failures
      console.error("❌ Finalize pipeline failed:", e);
      const info = manifest.autofillRetryInfo || {
        retryCount: 0,
        maxRetries: 3,
        lastRetryTime: null,
        runId: RUN_ID,
      };
      info.retryCount += 1;
      info.lastRetryTime = Date.now();
      manifest.autofillRetryInfo = info;
      manifest.lastAutofillError = String(e?.message || e);
      if (info.retryCount >= info.maxRetries) {
        manifest.autofillNonRetriableRunId = RUN_ID;
        console.error(
          `🚫 Autofill (finalize) failed ${info.retryCount} times — capping for this run.`
        );
      } else {
        console.warn(
          `⚠️ Autofill (finalize) failed (attempt ${info.retryCount}/${info.maxRetries}).`
        );
      }
      await idbPut("sessions", sessionId, manifest);
      consultationManager.removeProcessingPlaceholder(sessionId);
      return;
    } finally {
      busyState.end("filling");
      const manifestNow = await idbGet("sessions", sessionId);
      if (manifestNow && manifestNow.segments.every((s) => s.uploaded)) {
        if (manifestNow.completed) {
          await idbDelete("sessions", sessionId);
        }
      }
    }
  }

  return {
    async enqueueNewSegment(sessionId, seg, blob) {
      console.log("💾 Enqueueing segment:", {
        sessionId,
        seg,
        size: blob.size,
      });
      await idbPut("segments", seg.id, blob);
      const manifest = (await idbGet("sessions", sessionId)) || {
        sessionId,
        createdAtISO: new Date().toISOString(),
        status: "awaiting_upload",
        segments: [],
        transcriptionParts: [],
        retryInfo: {
          retryCount: 0,
          maxRetries: 20,
          lastRetryTime: null,
          retryInterval: 30000,
          runId: RUN_ID,
        },
        // ⭐ NEW: ensure these fields exist for new sessions
        autofillRetryInfo: {
          retryCount: 0,
          maxRetries: 3,
          lastRetryTime: null,
          runId: RUN_ID,
        },
        autofillNonRetriableRunId: undefined,
      };
      manifest.segments.push({
        ...seg,
        uploaded: false,
        size: blob.size,
        attempts: 0,
        nextAttemptAt: null,
        lastError: undefined,
        // ❗ mark non-retriable only per-run
        nonRetriableRunId: undefined,
      });
      await idbPut("sessions", sessionId, manifest);
      await flushSession(sessionId);
    },
  };
}
